import React, { useCallback } from 'react';
import { css, styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { SEARCH_MODE_SWITCHER_RIGHT_LOCATION } from '@atlassian/jira-jql-builder/src/common/constants.tsx';
import type {
	JQLBuilderProps,
	OmitJQLBuilderProps,
	EditorContainerProps,
} from '@atlassian/jira-jql-builder/src/common/types.tsx';

import JQLBuilder from '@atlassian/jira-jql-builder/src/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { JQLBuilderEmbedded$key as UserPreferencesFragmentKey } from '@atlassian/jira-relay/src/__generated__/JQLBuilderEmbedded.graphql';
import type { AIBuilderOverrides } from '../../../common/types.tsx';
import { SearchModeSwitcher } from './search-mode-switcher/index.tsx';

/**
 * Additional props for JQL generated by embedded natural language search.
 */
export type JQLBuilderEmbeddedProps = OmitJQLBuilderProps<
	JQLBuilderProps,
	'userPreferencesData'
> & {
	aiBuilderOverrides?: AIBuilderOverrides;
	generatedJql: string;
	userPreferences: UserPreferencesFragmentKey | null;
};

// Workarpound for Omit in 3.5 breaking Discriminated Unions (see https://github.com/microsoft/TypeScript/issues/31501#issuecomment-1280579305)
export type OmitJQLBuilderEmbeddedProps<
	T,
	K extends keyof JQLBuilderEmbeddedProps,
> = T extends JQLBuilderEmbeddedProps ? Omit<T, K> : never;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, jira/styled/styled-component-order
const CustomEditorContainer = styled.div<EditorContainerProps>(
	{
		display: 'flex',
		flexGrow: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.showAdvancedEditor
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					marginTop: token('space.negative.050'),
					marginRight: token('space.negative.050'),
					marginBottom: token('space.negative.050'),
					marginLeft: token('space.negative.050'),
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					/* ExtraControls should wrap with refinements when we are NOT in advanced mode */
					flexWrap: 'wrap',
					marginTop: token('space.negative.050'),
					marginRight: token('space.negative.050'),
					marginBottom: 0,
					marginLeft: token('space.negative.050'),
				}),
);

const components = {
	SearchModeSwitcher,
	EditorContainer: CustomEditorContainer,
};

const JQLBuilderEmbedded = ({
	aiBuilderOverrides,
	generatedJql,
	query,
	jqlMessages,
	onSearch,
	renderExtraControls,
	userPreferences,
	...props
}: JQLBuilderEmbeddedProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleSearch = useCallback(
		(jql: string) => {
			onSearch && onSearch(jql);
			const isModified = generatedJql !== jql;
			const event = createAnalyticsEvent({
				action: 'submitted',
				actionSubject: 'search',
			});
			fireUIAnalytics(event, 'jqlBuilderEmbedded', { isModified });
		},
		[createAnalyticsEvent, generatedJql, onSearch],
	);

	// Allow renderExtraControls to be overridden for the embedded AI view.
	const renderExtraControlsOverride =
		aiBuilderOverrides?.renderExtraControls ?? renderExtraControls;

	const userPreferencesData = useFragment<UserPreferencesFragmentKey>(
		graphql`
			fragment JQLBuilderEmbedded on JiraUserPreferences {
				...main_jqlBuilder_JQLBuilder
			}
		`,
		userPreferences,
	);

	return (
		<JQLBuilder
			{...props}
			userPreferencesData={userPreferencesData ?? null}
			query={query}
			jqlMessages={jqlMessages}
			onSearch={onSearch ? handleSearch : undefined}
			renderExtraControls={renderExtraControlsOverride}
			components={components}
			searchModeSwitcherLocation={SEARCH_MODE_SWITCHER_RIGHT_LOCATION}
		/>
	);
};

export default JQLBuilderEmbedded;
